import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {UrlStore} from '../shared/url-store';
import {TenantSettingsResponse} from './shared/models/tenant-settings.model';
import {MarketsConfigResponse, MarketsSavePayload} from './shared/models/markets-config.model';

// One configured OD group: a parent OD and one of its child ODs. Mirrors a row of
// rail.od_grouping, which the batch app reads directly.
export interface OdGroup {
    parentOrigin: string;
    parentDestination: string;
    childOrigin: string;
    childDestination: string;
}

@Injectable({
    providedIn: 'root'
})
export class ConfigurationService {
    constructor(private http: HttpClient) {
    }

    // Loads the saved configuration sets from the user_configuration table, keyed by name.
    public getConfiguration(): Observable<{[name: string]: any}> {
        return this.http.get<{[name: string]: any}>(UrlStore.api.userConfiguration);
    }

    // Persists a flat map of named configuration sets to the user_configuration table.
    public saveConfiguration(payload: {[name: string]: any}): Observable<any> {
        return this.http.post(UrlStore.api.userConfiguration, payload);
    }

    // OD groups live in their own table rather than in user_configuration, because the
    // batch app and the inventory screens read that table directly.
    public getOdGroups(): Observable<OdGroup[]> {
        return this.http.get<OdGroup[]>(UrlStore.api.odGrouping);
    }

    // Replaces the configured OD groups with the supplied list. The API validates the
    // whole list and writes nothing if it is invalid, so a 400 leaves the saved groups
    // exactly as they were.
    public saveOdGroups(groups: OdGroup[]): Observable<OdGroup[]> {
        return this.http.post<OdGroup[]>(UrlStore.api.odGrouping, groups);
    }

    // Reads the tenant's installed settings.json. Admin-only server side, and the API
    // masks credentials-adjacent values before they reach us.
    public getTenantSettings(): Observable<TenantSettingsResponse> {
        return this.http.get<TenantSettingsResponse>(UrlStore.api.tenantSettings);
    }

    // Not Admin-gated server side - anyone with access to the Configuration screen can
    // view market definitions, per the Markets screen design.
    public getMarketsConfig(): Observable<MarketsConfigResponse> {
        return this.http.get<MarketsConfigResponse>(UrlStore.api.marketsConfig);
    }

    // Validates and replaces the tenant's market configuration. Not Admin-gated either - same
    // access population as the rest of the Configuration screen.
    public saveMarketsConfig(payload: MarketsSavePayload): Observable<MarketsConfigResponse> {
        return this.http.post<MarketsConfigResponse>(UrlStore.api.marketsConfig, payload);
    }
}
